/**
 * Mixed-direction lines (v1 ticket 24), DOM-free so a unit test can read it.
 *
 * An interface line often quotes the target: «¿Me pones…?» یعنی چی؟ in the
 * Persian interface, or What does “ghahve (قهوه)” mean? in the English one.
 * `dir="auto"` takes its direction from the first strong letter, so a Persian
 * line that opens on a Spanish quote would run left to right, and the quote's
 * own neutral marks (¿ … ? and the brackets) would land on the wrong side.
 * `lineDir` gives such a line the interface's direction; `scriptRuns` finds the
 * runs in the other script, which the view isolates in their own direction.
 */

const RTL_LETTER = /[֐-ࣿיִ-﷿ﹰ-﻿]/;
const LATIN_LETTER = /[A-Za-zÀ-ɏ]/;

/**
 * Latin runs inside a right-to-left line: an opening ¿ or ¡, letters, digits
 * and the marks words carry inside a phrase (a slash too: p/t/k), ending on a letter or digit; a
 * closing … ? ! . or , joins the run only right before a closing quote or
 * bracket, or at the end of the line, where it is the quote's own (“A latte,” she says).
 */
const LATIN_RUN =
  /[¿¡]?[A-Za-zÀ-ɏ](?:[A-Za-zÀ-ɏ0-9'’\-·.,…!?;:/ ]*[A-Za-zÀ-ɏ0-9])?(?:[.…!?,]+(?=["”»)\]’]|$))?/g;

/** Arabic-script runs inside a left-to-right line, the same way (ZWNJ, Persian punctuation and digits inside). */
const ARABIC_RUN =
  /[ء-ٟٮ-ۓەۺ-ۿﭐ-﷿ﹰ-﻿](?:[؀-ۿﭐ-﷿ﹰ-﻿‌‍0-9 .,!?…\-]*[ء-ٟٮ-ۓە۰-ۿﭐ-﷿ﹰ-﻿0-9])?(?:[.…!?؟،]+(?=["”»)\]’]|$))?/g;

export type Run = { text: string; other: boolean };

/**
 * The line cut into runs: `other` marks a run in the script that is not the
 * interface's (Latin in the Persian interface, Arabic script in the English
 * one). A line without both scripts comes back whole, unmarked.
 */
export function scriptRuns(text: string, rtlInterface: boolean): Run[] {
  const own = rtlInterface ? RTL_LETTER : LATIN_LETTER;
  const other = rtlInterface ? LATIN_LETTER : RTL_LETTER;
  if (!own.test(text) || !other.test(text)) return [{ text, other: false }];
  const out: Run[] = [];
  let at = 0;
  for (const m of text.matchAll(rtlInterface ? LATIN_RUN : ARABIC_RUN)) {
    if (m.index! > at) out.push({ text: text.slice(at, m.index), other: false });
    out.push({ text: m[0], other: true });
    at = m.index! + m[0].length;
  }
  if (at < text.length) out.push({ text: text.slice(at), other: false });
  return out;
}

/**
 * The direction of an interface line: the interface's own when the line holds
 * a letter of its script, else `auto` (a line all in the target keeps the
 * target's direction).
 */
export function lineDir(text: string, rtlInterface: boolean): 'rtl' | 'ltr' | 'auto' {
  if (rtlInterface) return RTL_LETTER.test(text) ? 'rtl' : 'auto';
  return LATIN_LETTER.test(text) ? 'ltr' : 'auto';
}

/** An arrow that points back (or on) in the interface's direction. */
export function backArrow(rtlInterface: boolean): string {
  return rtlInterface ? '→' : '←';
}
export function onArrow(rtlInterface: boolean): string {
  return rtlInterface ? '←' : '→';
}
